import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../../../../components/Loader";
import { convertDateWithDayName } from '../../../../../scripts/utils';
import { useTranslation } from "react-i18next";

export default function TypePostSurvey({ data, people, setDataGraph, setFormattedHeader, filteredPosts, user }) {
    const [postSurvey, setPostSurvey] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPostPage, setCurrentPostPage] = useState({});
    const [postItemsPerPage, setPostItemPerPage] = useState(5);
    const itemsPerPage = 5;
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;

    const canUseCallerId = user && user.holdingId === 33;

    // Normaliser filteredPosts : peut être un tableau (holding 33) ou un objet simple {posts, calltype}
    // On le convertit toujours en { posts: [], calltype: "", personNames: [] }
    const normalizedFilteredPosts = useMemo(() => {
        if (Array.isArray(filteredPosts)) {
            const allPosts = filteredPosts.flatMap(entry => entry.posts || []);
            const calltype = filteredPosts[0]?.calltype || '';
            // personNames pour valider callinglineid et bloquer les numéros partagés
            const personNames = filteredPosts
                .map(entry => entry.personName?.trim().toLowerCase())
                .filter(Boolean);
            return { posts: allPosts, calltype, personNames };
        }
        return {
            posts: filteredPosts?.posts || [],
            calltype: filteredPosts?.calltype || '',
            personNames: []
        };
    }, [filteredPosts]);

    // Map personne par numéro de poste pour lookups rapides
    const peopleByNumber = useMemo(() => {
        const map = new Map();
        people.flat().forEach(person => {
            [person.phone?.work, person.phone?.mobile].filter(n => n).forEach(num => {
                map.set(num, person);
            });
        });
        return map;
    }, [people]);

    const uniquePosts = useMemo(() => {
        const knownNumbers = new Set(
            people.flat().map(item => item.phone?.work || item.phone?.mobile).filter(n => n)
        );
        const activePosts = new Set();

        data.forEach(item => {
            // ✅ L'appartenance est TOUJOURS déterminée par callingnumber (le vrai poste)
            // Le calleridnumber est uniquement un numéro d'affichage — potentiellement partagé
            if (knownNumbers.has(item.callingnumber)) {
                activePosts.add(item.callingnumber);
            }
        });

        return Array.from(activePosts);
    }, [data, people]);

    /**
     * Résolution du filtre : si le filtre contient des calleridnumbers (numéros affichés),
     * on les résout vers leur callingnumber réel en cherchant dans les données.
     * Cela garantit qu'un calleridnumber partagé (ex: +33582954300 utilisé par Annie ET Valérie)
     * sera toujours résolu vers le BON callingnumber selon la personne sélectionnée.
     */
    const finalPosts = useMemo(() => {
        const { posts, personNames } = normalizedFilteredPosts;
        if (posts.length === 0) return uniquePosts;

        const resolvedPosts = new Set();

        posts.forEach(num => {
            if (peopleByNumber.has(num)) {
                // C'est directement un numéro de poste
                // Si personNames est fourni (holding 33), valider que ce poste appartient bien
                // à une des personnes sélectionnées (bloquer les numéros partagés)
                if (personNames.length > 0) {
                    const person = peopleByNumber.get(num);
                    const personFullName = `${person?.firstName} ${person?.lastName}`.trim().toLowerCase();
                    if (personNames.includes(personFullName)) {
                        resolvedPosts.add(num);
                    }
                } else {
                    resolvedPosts.add(num);
                }
            } else if (canUseCallerId) {
                // C'est un calleridnumber → résoudre vers le callingnumber correspondant
                // Si personNames est fourni, valider que le callingnumber appartient à la bonne personne
                const matchItem = data.find(item => {
                    if (item.calleridnumber !== num || !peopleByNumber.has(item.callingnumber)) return false;
                    if (personNames.length > 0) {
                        const person = peopleByNumber.get(item.callingnumber);
                        const personFullName = `${person?.firstName} ${person?.lastName}`.trim().toLowerCase();
                        return personNames.includes(personFullName);
                    }
                    return true;
                });
                if (matchItem) {
                    resolvedPosts.add(matchItem.callingnumber);
                }
            }
        });

        return Array.from(resolvedPosts);
    }, [normalizedFilteredPosts, uniquePosts, peopleByNumber, data, canUseCallerId]);

    useEffect(() => {
        if (data.length > 0) {
            handleGraphPostSurvey(data);
            setCurrentPostPage(1);
        }
    }, [data, filteredPosts]);

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const isBusyCall = (call) => {
        const busyValues = [
            call?.calloutcomereason,
            call?.originalreason,
            call?.redirectreason,
            call?.relatedreason
        ].map(value => String(value || '').toLowerCase());

        return busyValues.some(value => value.includes('busy'));
    };

    const getCallResultLabel = (call) => {
        if (isBusyCall(call)) {
            return 'Busy';
        }

        return call?.answerindicator || 'No';
    };

    /**
     * SÉCURITÉ CENTRALE — Vérifie qu'un item appartient bien à une personne donnée.
     * Pour les appels sortants : callingnumber doit correspondre au poste ET
     *   si holding 33 avec filtre actif, callinglineid doit correspondre au nom de la personne.
     * Pour les appels entrants : callednumber suffit (callinglineid = l'appelant externe, pas le poste).
     * Le calleridnumber n'est JAMAIS utilisé pour déterminer l'appartenance.
     */
    const itemBelongsToPostOutgoing = (item, postNum, personFullName) => {
        if (item.callingnumber !== postNum) return false;
        // Holding 33 avec personName actif : on valide callinglineid pour bloquer les numéros partagés
        if (canUseCallerId && personFullName) {
            const itemName = (item.callinglineid || "").trim().toLowerCase();
            if (itemName && itemName !== personFullName) return false;
        }
        return true;
    };

    const itemBelongsToPostIncoming = (item, postNum) => {
        // Entrant : la personne est le destinataire → callednumber
        return item.callednumber === postNum;
    };

    const handleGraphPostSurvey = (data) => {
        const { personNames } = normalizedFilteredPosts;

        let postData = finalPosts.map(post => {
            const personRef = peopleByNumber.get(post) || null;
            const personFullName = personRef
                ? `${personRef.firstName} ${personRef.lastName}`.trim().toLowerCase()
                : null;
            const validPersonName = personNames.length > 0
                ? personNames.find(n => n === personFullName) || null
                : personFullName;

            const callerIdUsed = new Set();
            if (canUseCallerId) {
                data.forEach(item => {
                    if (itemBelongsToPostOutgoing(item, post, validPersonName) && item.calleridnumber) {
                        callerIdUsed.add(item.calleridnumber);
                    }
                });
            }

            return {
                post,
                personRef,
                personFullName: validPersonName,
                callerIdNumbers: Array.from(callerIdUsed),
                callinglineid: personRef ? `${personRef.firstName} ${personRef.lastName}` : "",
                isCallerId: callerIdUsed.size > 0,
                Outgoing: {
                    total: 0, durationTotal: 0, averageduration: 0,
                    response: 0, averageresponse: 0, noresponse: 0, busy: 0
                },
                Incoming: {
                    total: 0, durationTotal: 0, averageduration: 0,
                    response: 0, averageresponse: 0, noresponse: 0, busy: 0
                },
                Total: { durationTotal: 0, responseTotal: 0 },
                data: []
            };
        });

        data.forEach(item => {
            // ─── SORTANTS ───────────────────────────────────────────────────────────
            // Holding 33 : les données CDR contiennent les deux perspectives de chaque
            // appel interne (ORIGINATING du 575 ET TERMINATING du 534 avec callingnumber="575").
            // On restreint aux ORIGINATING pour éviter le double comptage.
            // Autres holdings : pas de double perspective → pas de restriction sur direction.
            const matchSortant = canUseCallerId
                ? item.direction === "ORIGINATING"
                : true;

            if (matchSortant) {
                if (normalizedFilteredPosts.calltype !== '' ? normalizedFilteredPosts.calltype.includes('sortants') : true) {
                    const postIndex = postData.findIndex(p =>
                        itemBelongsToPostOutgoing(item, p.post, p.personFullName)
                    );
                    if (postIndex !== -1) {
                        postData[postIndex].data.push(item);
                        postData[postIndex].Outgoing.total += 1;
                        postData[postIndex].Outgoing.durationTotal += Math.floor(parseInt(item.duration) || 0);
                        if (isBusyCall(item)) {
                            postData[postIndex].Outgoing.busy += 1;
                        } else if (item.answerindicator === "Yes") {
                            postData[postIndex].Outgoing.response += 1;
                            postData[postIndex].Outgoing.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                        } else if (item.answerindicator === "No") {
                            postData[postIndex].Outgoing.noresponse += 1;
                        }
                    } else if (item.callingnumber && peopleByNumber.has(item.callingnumber)) {
                        const rejectedPerson = peopleByNumber.get(item.callingnumber);
                        console.warn(`🚫 [TypePostSurvey] SORTANT REFUSÉ — callingnumber: ${item.callingnumber} | callinglineid: "${item.callinglineid}" | calleridnumber: ${item.calleridnumber} | personne déduite: ${rejectedPerson?.firstName} ${rejectedPerson?.lastName}`);
                    }
                }
            }

            // ─── ENTRANTS ───────────────────────────────────────────────────────────
            // Holding 33 : même problème côté entrant — l'enregistrement ORIGINATING
            // d'un autre poste a aussi calledNumber="575" → double comptage sans restriction.
            // Autres holdings : pas de restriction.
            const matchEntrant = canUseCallerId
                ? item.direction === "TERMINATING"
                : true;

            if (matchEntrant) {
                if (normalizedFilteredPosts.calltype !== '' ? normalizedFilteredPosts.calltype.includes('entrants') : true) {
                    const postIndex = postData.findIndex(p =>
                        itemBelongsToPostIncoming(item, p.post)
                    );
                    if (postIndex !== -1) {
                        if (!postData[postIndex].data.includes(item)) {
                            postData[postIndex].data.push(item);
                        }
                        postData[postIndex].Incoming.total += 1;
                        postData[postIndex].Incoming.durationTotal += Math.floor(parseInt(item.duration) || 0);
                        if (isBusyCall(item)) {
                            postData[postIndex].Incoming.busy += 1;
                        } else if (item.answerindicator === "Yes") {
                            postData[postIndex].Incoming.response += 1;
                            postData[postIndex].Incoming.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
                        } else if (item.answerindicator === "No") {
                            postData[postIndex].Incoming.noresponse += 1;
                        }
                    }
                }
            }
        });

        postData.forEach(post => {
            let Outgoing = post.Outgoing;
            let Incoming = post.Incoming;
            let Total = post.Total;

            Outgoing.averageduration = Outgoing.total > 0 ? Outgoing.durationTotal / Outgoing.total : 0;
            Outgoing.averageresponse = Outgoing.response > 0 ? Outgoing.averageresponse / Outgoing.response : 0;

            Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
            Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;

            Total.responseTotal = Outgoing.response + Incoming.response + Outgoing.noresponse + Incoming.noresponse + Outgoing.busy + Incoming.busy;
            Total.durationTotal = formatDuration(Outgoing.durationTotal + Incoming.durationTotal);

            Outgoing.durationTotal = formatDuration(Outgoing.durationTotal);
            Outgoing.averageduration = formatDuration(Outgoing.averageduration);
            Outgoing.averageresponse = formatDuration(Outgoing.averageresponse);

            Incoming.durationTotal = formatDuration(Incoming.durationTotal);
            Incoming.averageduration = formatDuration(Incoming.averageduration);
            Incoming.averageresponse = formatDuration(Incoming.averageresponse);
        });

        postData = postData.filter(post =>
            post.Outgoing.total > 0 || post.Incoming.total > 0 || post.data.length > 0
        );

        setPostSurvey(postData);
        const formattedData = generateExcelData(postData, currentLang);
        setDataGraph(formattedData);
    };

    const generateExcelData = (posts, lang) => {
        const formattedData = [];
        formattedData.push(["", "Sortant", "Entrant", "Total", "", "", "", "", "", "", ""]);

        posts.forEach(post => {
            const displayName = (post.isCallerId ? "⛔ " : "") + (post.callinglineid || post.post);
            formattedData.push([displayName, "", "", "", "", ""]);
            formattedData.push(["Nombre d'appels", post.Outgoing.total || 0, post.Incoming.total || 0, ""]);
            formattedData.push(["Nombre d'appels répondu", post.Outgoing.response || 0, post.Incoming.response || 0, post.Total.responseTotal || 0]);
            formattedData.push(["Durée totale d'appels", post.Outgoing.durationTotal || "00:00:00", post.Incoming.durationTotal || "00:00:00", post.Total.durationTotal || "00:00:00"]);
            formattedData.push(["Durée moyenne pour répondre", post.Outgoing.averageresponse || "00:00:00", post.Incoming.averageresponse || "00:00:00", ""]);
            formattedData.push(["Nombre d'appels non répondu", post.Outgoing.noresponse || 0, post.Incoming.noresponse || 0, ""]);
            formattedData.push(["Nombre d'appels Busy", post.Outgoing.busy || 0, post.Incoming.busy || 0, ""]);
            formattedData.push(["Durée moyenne d'appels", post.Outgoing.averageduration || "00:00:00", post.Incoming.averageduration || "00:00:00", ""]);
            const headers = [
                "Date", "Durée de communication", "Poste", "Nom poste", "Numéro",
                ...(canUseCallerId ? ["Numéro affiché"] : []),
                "Nom", "Origine", "Résultat", "Durée d'attente", "Type"
            ];
            formattedData.push(headers);

            post.data.forEach(call => {
                formattedData.push([
                    convertDateWithDayName(call.starttime, lang) || "",
                    call.duration || "0",
                    call.callingnumber || "",
                    post.callinglineid || "",
                    call.callednumber || "",
                    ...(canUseCallerId ? [call.calleridnumber || ""] : []),
                    call.calledlineid || "",
                    getCallType(call.calltype),
                    getCallResultLabel(call),
                    call.ringduration || "0",
                    getCallDirection(call.direction)
                ]);
            });
            formattedData.push([]);
        });

        setFormattedHeader(true);
        return formattedData;
    };

    const getCallDirection = (direction) => {
        switch (direction) {
            case 'TERMINATING': return t('Entrant');
            case 'ORIGINATING': return t('Sortant');
            default: return direction;
        }
    };

    const getCallType = (callType) => {
        switch (callType) {
            case 'SIP_ENTERPRISE': return 'Interne';
            case 'SIP_NATIONAL': return 'National';
            case 'SIP_MOBILE': return 'Mobile';
            case 'SIP_INTERNATIONAL': return 'International';
            case 'SIP_PREMIUM': return 'Premium';
            case 'SIP_INBOUND': return 'Entrant';
            case 'SIP_OPERATOR': return 'Opérateur';
            case 'SIP_TOLLFREE': return 'Sans frais';
            case 'SIP_MEETING': return 'Réunion';
            case 'UNKNOWN': return 'Inconnu';
            default: return callType;
        }
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const paginatePost = (postKey, pageNumber) => {
        setCurrentPostPage(prev => ({ ...prev, [postKey]: pageNumber }));
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const changePostItemPerPage = (change) => {
        setPostItemPerPage(prevValue => {
            const newValue = prevValue + change;
            if (newValue < 1) return 1;
            if (newValue > 15) return 15;
            return newValue;
        });
    };

    if (postSurvey.length === 0) {
        return <div>{t("Aucune donnée disponible")}</div>;
    }

    return (
        <div className="postSurvey">
            {postSurvey.length > 0 ? (
                <>
                    <div className="pagination-surveyPost">
                        <button
                            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : currentPage)}
                            disabled={currentPage === 1}
                        >
                            {t("Précédent")}
                        </button>
                        <button
                            onClick={() => paginate(currentPage < Math.ceil(postSurvey.length / itemsPerPage) ? currentPage + 1 : currentPage)}
                            disabled={currentPage === Math.ceil(postSurvey.length / itemsPerPage)}
                        >
                            {t("Suivant")}
                        </button>
                    </div>
                    {postSurvey.slice(indexOfFirstItem, indexOfLastItem).map((post) => (
                        <section className="postSurvey-element" key={post.post}>
                            <div>
                                <table className="postSurvey-sortants">
                                    <thead>
                                        <tr className="sortant"><th colSpan={7}>{t("Sortants")}</th></tr>
                                        <tr className="sortant-head">
                                            <th title={t("Poste")}>{t("Poste")}</th>
                                            <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                            <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                            <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                            <th title={t("Nombre d'appels Busy")}>{t("Nombre d'appels Busy")}</th>
                                            <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                            <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="sortant-body">
                                            <td>
                                                {post.isCallerId ? "⛔ " : ""}
                                                {post.callinglineid || post.post}
                                            </td>
                                            <td>{post.Outgoing.response}</td>
                                            <td>{post.Outgoing.averageresponse}</td>
                                            <td>{post.Outgoing.noresponse}</td>
                                            <td>{post.Outgoing.busy}</td>
                                            <td>{post.Outgoing.durationTotal}</td>
                                            <td>{post.Outgoing.averageduration}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className="postSurvey-entrants">
                                    <thead>
                                        <tr className="entrant"><th colSpan={6}>{t("Entrants")}</th></tr>
                                        <tr>
                                            <th title={t("Nombre d'appels répondu")}>{t("Nombre d'appels répondu")}</th>
                                            <th title={t("Durée moyenne pour répondre")}>{t("Durée moyenne pour répondre")}</th>
                                            <th title={t("Nombre d'appels non répondu")}>{t("Nombre d'appels non répondu")}</th>
                                            <th title={t("Nombre d'appels Busy")}>{t("Nombre d'appels Busy")}</th>
                                            <th title={t("Durée totale d'appels")}>{t("Durée totale d'appels")}</th>
                                            <th title={t("Durée moyenne d'appels")}>{t("Durée moyenne d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{post.Incoming.response}</td>
                                            <td>{post.Incoming.averageresponse}</td>
                                            <td>{post.Incoming.noresponse}</td>
                                            <td>{post.Incoming.busy}</td>
                                            <td>{post.Incoming.durationTotal}</td>
                                            <td>{post.Incoming.averageduration}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <table className="postSurvey-total">
                                    <thead className="postSurvey-total-head">
                                        <tr className="total"><th colSpan={5}>{t("Total")}</th></tr>
                                        <tr>
                                            <th title={t("Durée totale des appels")}>{t("Durée totale des appels")}</th>
                                            <th title={t("Nombre totale d'appels")}>{t("Nombre totale d'appels")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="postSurvey-total-body">
                                        <tr>
                                            <td>{post.Total.durationTotal}</td>
                                            <td>{post.Total.responseTotal}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="postSurvey-post-title">{t("Détails des appels")} :</p>
                            <div>
                                <table className="postSurvey-post">
                                    <thead>
                                        <tr className="postSurvey-post-head">
                                            <th>{t("Date")}</th>
                                            <th>{t("Durée de communication")}</th>
                                            <th>{t("Poste")}</th>
                                            <th>{t("Nom poste")}</th>
                                            <th>{t("Numéro")}</th>
                                            {canUseCallerId && <th>{t("Numéro affiché")}</th>}
                                            <th>{t("Nom")}</th>
                                            <th>{t("Origine")}</th>
                                            <th>{t("Résultat")}</th>
                                            <th title={t("Durée d'attente")}>{t("Durée d'attente")}</th>
                                            <th>{t("Type")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {post.data
                                            .filter(item => {
                                                const isOutgoing = item.callingnumber === post.post;
                                                const isIncoming = item.callednumber === post.post;
                                                if (!isOutgoing && !isIncoming) return false;
                                                if (isOutgoing && canUseCallerId && post.personFullName) {
                                                    const itemName = (item.callinglineid || "").trim().toLowerCase();
                                                    if (itemName && itemName !== post.personFullName) return false;
                                                }
                                                return true;
                                            })
                                            .slice(
                                                (currentPostPage[post.post] || 1) * postItemsPerPage - postItemsPerPage,
                                                (currentPostPage[post.post] || 1) * postItemsPerPage
                                            )
                                            .map((item, idx) => (
                                                <tr className="postSurvey-post-body" key={idx}>
                                                    <td>{convertDateWithDayName(item.starttime, currentLang)}</td>
                                                    <td>{item.duration}</td>
                                                    <td>{item.callingnumber}</td>
                                                    <td>{post.callinglineid}</td>
                                                    <td>{item.callednumber}</td>
                                                    {canUseCallerId && <td>{item.calleridnumber}</td>}
                                                    <td>{item.calledlineid}</td>
                                                    <td>{getCallType(item.calltype)}</td>
                                                    <td>{getCallResultLabel(item)}</td>
                                                    <td>{item.ringduration}</td>
                                                    <td>{getCallDirection(item.direction)}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pagination-surveyPost-post">
                                <button
                                    onClick={() =>
                                        paginatePost(post.post, (currentPostPage[post.post] || 1) > 1
                                            ? (currentPostPage[post.post] || 1) - 1
                                            : 1)
                                    }
                                    disabled={(currentPostPage[post.post] || 1) === 1}
                                >
                                    {t("Précédent")}
                                </button>
                                <button
                                    onClick={() =>
                                        paginatePost(post.post, (currentPostPage[post.post] || 1) < Math.ceil(post.data.length / postItemsPerPage)
                                            ? (currentPostPage[post.post] || 1) + 1
                                            : (currentPostPage[post.post] || 1))
                                    }
                                    disabled={(currentPostPage[post.post] || 1) === Math.ceil(post.data.length / postItemsPerPage)}
                                >
                                    {t("Suivant")}
                                </button>
                                <div className="surveyPost-post-numbers">
                                    <button onClick={() => changePostItemPerPage(-1)} disabled={postItemsPerPage === 1}>-</button>
                                    <span>{postItemsPerPage}</span>
                                    <button onClick={() => changePostItemPerPage(+1)} disabled={postItemsPerPage === 15}>+</button>
                                </div>
                            </div>
                        </section>
                    ))}
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
}
